/**
 * Hook pour gérer une table brouillon (draft) via Socket.IO
 * 
 * Gère la communication temps réel avec le backend pour :
 * - Créer/rejoindre/quitter un draft
 * - Modifier la configuration collaborativement
 * - Gérer les joueurs (inviter, éjecter)
 * - Chat intégré
 * - Finaliser la création de la table
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import type {
  DraftTable,
  DraftTableConfig,
  DraftPlayer,
  DraftMessage,
  DraftGameMode,
  SeatPosition,
} from '../types/draft';

/** Append messages that don't already exist (by id) */
function mergeMessages(existing: DraftMessage[], incoming: DraftMessage[]): DraftMessage[] {
  if (incoming.length === 0) return existing;
  const ids = new Set(existing.map(m => m.id));
  const novel = incoming.filter(m => !ids.has(m.id));
  return novel.length > 0 ? [...existing, ...novel] : existing;
}

export interface UseDraftTableOptions {
  socket: Socket | null;
  pseudo: string;
  compet: boolean;
}

export interface UseDraftTableReturn {
  // État
  draft: DraftTable | null;
  isInDraft: boolean;
  isAdmin: boolean;
  error: string | null;

  // Actions
  createDraft: (config?: Partial<DraftTableConfig>, invitedPlayers?: string[]) => void;
  joinDraft: (draftId: string) => void;
  leaveDraft: () => void;
  updateConfig: (config: Partial<DraftTableConfig>) => void;
  updateSeats: (seats: Array<{ playerPseudo: string; seat: SeatPosition; tableIndex?: number }>) => void;
  invitePlayer: (targetPseudo: string) => void;
  kickPlayer: (targetPseudo: string) => void;
  sendMessage: (text: string) => void;
  switchMode: (gameMode: DraftGameMode) => void;
  finalize: () => void;
  clearError: () => void;
  closeDraft: () => void;
  detachDraft: () => void;
}

export function useDraftTable({ socket, pseudo, compet }: UseDraftTableOptions): UseDraftTableReturn {
  const [draft, setDraft] = useState<DraftTable | null>(null);
  const [error, setError] = useState<string | null>(null);
  const draftIdRef = useRef<string | null>(null);

  const isInDraft = draft !== null;
  const isAdmin = draft?.players.find(p => p.pseudo.toLowerCase() === pseudo.toLowerCase())?.isAdmin ?? false;

  // ==========================================
  // Listeners Socket.IO
  // ==========================================

  useEffect(() => {
    if (!socket) return;

    // Draft créé
    const onCreated = (data: { draft: DraftTable }) => {
      setDraft(data.draft);
      draftIdRef.current = data.draft.id;
      sessionStorage.setItem('wt_active_draft', data.draft.id);
      setError(null);
    };

    // État complet reçu (lors du join)
    const onState = (data: { draft: DraftTable }) => {
      setDraft(data.draft);
      draftIdRef.current = data.draft.id;
      sessionStorage.setItem('wt_active_draft', data.draft.id);
      setError(null);
    };

    // Configuration mise à jour (peut aussi inclure players si les seats ont changé, ex: réduction de tables)
    const onConfigUpdated = (data: { config: DraftTableConfig; players?: DraftPlayer[]; updatedBy: string; messages: DraftMessage[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          config: data.config,
          ...(data.players ? { players: data.players } : {}),
          messages: mergeMessages(prev.messages, data.messages),
        };
      });
    };

    // Joueur rejoint
    const onPlayerJoined = (data: { player: DraftPlayer; messages: DraftMessage[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        const existingIdx = prev.players.findIndex(p => p.pseudo.toLowerCase() === data.player.pseudo.toLowerCase());
        const players = existingIdx >= 0
          ? prev.players.map((p, i) => i === existingIdx ? data.player : p)
          : [...prev.players, data.player];
        return {
          ...prev,
          players,
          messages: mergeMessages(prev.messages, data.messages),
        };
      });
    };

    // Joueur parti
    const onPlayerLeft = (data: { pseudo: string; messages: DraftMessage[]; players: DraftPlayer[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          players: data.players,
          messages: mergeMessages(prev.messages, data.messages),
        };
      });
    };

    // Joueur éjecté
    const onPlayerKicked = (data: { pseudo: string; messages: DraftMessage[]; players: DraftPlayer[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          players: data.players,
          messages: mergeMessages(prev.messages, data.messages),
        };
      });
    };

    // Joueur invité
    const onPlayerInvited = (data: { player: DraftPlayer; messages: DraftMessage[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          players: [...prev.players, data.player],
          messages: mergeMessages(prev.messages, data.messages),
        };
      });
    };

    // Invitation refusée
    const onInvitationDeclined = (data: { pseudo: string; messages: DraftMessage[]; players: DraftPlayer[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          players: data.players,
          messages: mergeMessages(prev.messages, data.messages),
        };
      });
      // Auto-hide the declined player after 5 seconds
      setTimeout(() => {
        setDraft(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            players: prev.players.filter(
              p => !(p.pseudo.toLowerCase() === data.pseudo.toLowerCase() && p.status === 'declined')
            ),
          };
        });
      }, 5000);
    };

    // Seats mis à jour
    const onSeatsUpdated = (data: { players: DraftPlayer[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return { ...prev, players: data.players };
      });
    };

    // Nouveau message
    const onNewMessage = (data: { message: DraftMessage }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: mergeMessages(prev.messages, [data.message]),
        };
      });
    };

    // Joueur déconnecté
    const onPlayerDisconnected = (data: { pseudo: string; players: DraftPlayer[] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return { ...prev, players: data.players };
      });
    };

    // Finalisation en cours
    const onFinalizing = () => {
      setDraft(prev => {
        if (!prev) return prev;
        return { ...prev, status: 'creating' };
      });
    };

    // Table finalisée
    const onFinalized = (data: { draftId: string; table: DraftTable['finalizedTable'] }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return { ...prev, status: 'created', finalizedTable: data.table };
      });
    };

    // Erreur de finalisation
    const onFinalizeError = (data: { error: string }) => {
      setDraft(prev => {
        if (!prev) return prev;
        return { ...prev, status: 'configuring' };
      });
      setError(data.error);
    };

    // Draft supprimé
    const onDeleted = () => {
      setDraft(null);
      draftIdRef.current = null;
      sessionStorage.removeItem('wt_active_draft');
    };

    // Éjecté du draft
    const onKicked = (data: { draftId: string; pseudo: string }) => {
      if (data.pseudo.toLowerCase() === pseudo.toLowerCase()) {
        setDraft(null);
        draftIdRef.current = null;
        sessionStorage.removeItem('wt_active_draft');
        setError('Vous avez été retiré de la table');
      }
    };

    // Erreur
    const onError = (data: { error: string }) => {
      setError(data.error);
    };

    socket.on('draft:created', onCreated);
    socket.on('draft:state', onState);
    socket.on('draft:config_updated', onConfigUpdated);
    socket.on('draft:player_joined', onPlayerJoined);
    socket.on('draft:player_left', onPlayerLeft);
    socket.on('draft:player_kicked', onPlayerKicked);
    socket.on('draft:player_invited', onPlayerInvited);
    socket.on('draft:invitation_declined', onInvitationDeclined);
    socket.on('draft:seats_updated', onSeatsUpdated);
    socket.on('draft:new_message', onNewMessage);
    socket.on('draft:player_disconnected', onPlayerDisconnected);
    socket.on('draft:finalizing', onFinalizing);
    socket.on('draft:finalized', onFinalized);
    socket.on('draft:finalize_error', onFinalizeError);
    socket.on('draft:deleted', onDeleted);
    socket.on('draft:kicked', onKicked);
    socket.on('draft:error', onError);

    return () => {
      socket.off('draft:created', onCreated);
      socket.off('draft:state', onState);
      socket.off('draft:config_updated', onConfigUpdated);
      socket.off('draft:player_joined', onPlayerJoined);
      socket.off('draft:player_left', onPlayerLeft);
      socket.off('draft:player_kicked', onPlayerKicked);
      socket.off('draft:player_invited', onPlayerInvited);
      socket.off('draft:invitation_declined', onInvitationDeclined);
      socket.off('draft:seats_updated', onSeatsUpdated);
      socket.off('draft:new_message', onNewMessage);
      socket.off('draft:player_disconnected', onPlayerDisconnected);
      socket.off('draft:finalizing', onFinalizing);
      socket.off('draft:finalized', onFinalized);
      socket.off('draft:finalize_error', onFinalizeError);
      socket.off('draft:deleted', onDeleted);
      socket.off('draft:kicked', onKicked);
      socket.off('draft:error', onError);
    };
  }, [socket, pseudo]);

  // ==========================================
  // Actions
  // ==========================================

  const createDraft = useCallback((config?: Partial<DraftTableConfig>, invitedPlayers?: string[]) => {
    if (!socket) return;
    socket.emit('draft:create', { pseudo, compet, config, invitedPlayers });
  }, [socket, pseudo, compet]);

  const joinDraft = useCallback((draftId: string) => {
    if (!socket) return;
    socket.emit('draft:join', { pseudo, draftId });
  }, [socket, pseudo]);

  const leaveDraft = useCallback(() => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:leave', { draftId: draftIdRef.current, pseudo });
    setDraft(null);
    draftIdRef.current = null;
    sessionStorage.removeItem('wt_active_draft');
  }, [socket, pseudo]);

  const updateConfig = useCallback((config: Partial<DraftTableConfig>) => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:update_config', { draftId: draftIdRef.current, pseudo, config });
  }, [socket, pseudo]);

  const updateSeats = useCallback((seats: Array<{ playerPseudo: string; seat: SeatPosition; tableIndex?: number }>) => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:update_seats', { draftId: draftIdRef.current, pseudo, seats });
  }, [socket, pseudo]);

  const invitePlayer = useCallback((targetPseudo: string) => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:invite', { draftId: draftIdRef.current, pseudo, targetPseudo });
  }, [socket, pseudo]);

  const kickPlayer = useCallback((targetPseudo: string) => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:kick', { draftId: draftIdRef.current, pseudo, targetPseudo });
  }, [socket, pseudo]);

  const sendMessage = useCallback((text: string) => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:message', { draftId: draftIdRef.current, pseudo, text });
  }, [socket, pseudo]);

  const switchMode = useCallback((gameMode: DraftGameMode) => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:switch_mode', { draftId: draftIdRef.current, pseudo, gameMode });
  }, [socket, pseudo]);

  const finalize = useCallback(() => {
    if (!socket || !draftIdRef.current) return;
    socket.emit('draft:finalize', { draftId: draftIdRef.current, pseudo });
  }, [socket, pseudo]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const closeDraft = useCallback(() => {
    if (draftIdRef.current) {
      leaveDraft();
    }
    setDraft(null);
    draftIdRef.current = null;
    sessionStorage.removeItem('wt_active_draft');
    setError(null);
  }, [leaveDraft]);

  /** Ferme l'éditeur localement sans quitter ni supprimer la table côté serveur. */
  const detachDraft = useCallback(() => {
    setDraft(null);
    draftIdRef.current = null;
    setError(null);
  }, []);

  return {
    draft,
    isInDraft,
    isAdmin,
    error,
    createDraft,
    joinDraft,
    leaveDraft,
    updateConfig,
    updateSeats,
    invitePlayer,
    kickPlayer,
    sendMessage,
    switchMode,
    finalize,
    clearError,
    closeDraft,
    detachDraft,
  };
}
